import React, { useState, useRef, useCallback } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { productService } from '../services/api'
import LoadingSpinner from '../components/LoadingSpinner'
import { Download, ArrowRight, Image as ImageIcon } from 'lucide-react'

// ─── Theme Definitions ────────────────────────────────────────────────────────
// Each theme has a template PNG and the bounding box where the product image goes.
// Coordinates are in the template's native pixel space.
// box: { x, y, w, h } = top-left origin, width, height of the white card zone.
const THEMES = [
  {
    id: 1,
    label: 'ثيم ١',
    sublabel: 'بوست',
    file: '/themes/theme1.png',
    canvasW: 1080,
    canvasH: 1350,
    box: { x: 30, y: 710, w: 1020, h: 615 },
  },
  {
    id: 2,
    label: 'ثيم ٢',
    sublabel: 'ستوري',
    file: '/themes/theme2.png',
    canvasW: 1080,
    canvasH: 1920,
    box: { x: 30, y: 990, w: 1020, h: 900 },
  },
  {
    id: 3,
    label: 'ثيم ٣',
    sublabel: 'ستوري',
    file: '/themes/theme3.png',
    canvasW: 1080,
    canvasH: 1920,
    box: { x: 30, y: 650, w: 1020, h: 1240 },
  },
  {
    id: 4,
    label: 'ثيم ٤',
    sublabel: 'بوست',
    file: '/themes/theme4.png',
    canvasW: 1080,
    canvasH: 1350,
    box: { x: 30, y: 490, w: 1020, h: 835 },
  },
]

// ─── Helpers ─────────────────────────────────────────────────────────────────

function getProductImageUrl(product) {
  if (product.images && product.images.length > 0) {
    return `/uploads/products/${product.images[0].filename}`
  }
  if (product.featured_image) {
    return `/uploads/products/${product.featured_image}`
  }
  return null
}

// Load an image from a URL, bypassing cache so canvas can taint-check
function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new window.Image()
    img.crossOrigin = 'anonymous'
    img.onload = () => resolve(img)
    img.onerror = reject
    // Append timestamp to force a fresh fetch so the browser sets CORS headers
    img.src = src.startsWith('http') ? src : `${src}?t=${Date.now()}`
  })
}

// Draw a rounded rectangle path
function roundRect(ctx, x, y, w, h, r) {
  ctx.beginPath()
  ctx.moveTo(x + r, y)
  ctx.lineTo(x + w - r, y)
  ctx.quadraticCurveTo(x + w, y, x + w, y + r)
  ctx.lineTo(x + w, y + h - r)
  ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h)
  ctx.lineTo(x + r, y + h)
  ctx.quadraticCurveTo(x, y + h, x, y + h - r)
  ctx.lineTo(x, y + r)
  ctx.quadraticCurveTo(x, y, x + r, y)
  ctx.closePath()
}

// Compose product image + template overlay on an off-screen canvas, return data URL
async function composeImage(theme, productImageSrc) {
  const { canvasW, canvasH, file, box } = theme

  const canvas = document.createElement('canvas')
  canvas.width = canvasW
  canvas.height = canvasH
  const ctx = canvas.getContext('2d')

  // 1. Draw product image first (behind the template)
  if (productImageSrc) {
    const product = await loadImage(productImageSrc)
    const { x, y, w, h } = box

    // cover-fit scaled up by 1.3× to fill the box
    const scaleX = (w * 1.3) / product.naturalWidth
    const scaleY = (h * 1.3) / product.naturalHeight
    const scale = Math.max(scaleX, scaleY)
    const dw = product.naturalWidth * scale
    const dh = product.naturalHeight * scale
    const dx = x + (w - dw) / 2
    const dy = y + (h - dh) / 2
    ctx.drawImage(product, dx, dy, dw, dh)
  }

  // 2. Draw template PNG on top (transparent window reveals product below)
  const template = await loadImage(file)
  ctx.drawImage(template, 0, 0, canvasW, canvasH)

  return canvas.toDataURL('image/jpeg', 0.95)
}

// ─── Small card preview drawn with a real canvas element ────────────────────

const ThemeCard = ({ theme, product, onDownload, downloading }) => {
  const previewRef = useRef(null)
  const [previewReady, setPreviewReady] = useState(false)
  const [hasError, setHasError] = useState(false)
  const productImgSrc = getProductImageUrl(product)

  // Draw preview lazily via IntersectionObserver
  const observerRef = useCallback(
    (node) => {
      if (!node) return
      const observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            observer.disconnect()
            renderPreview()
          }
        },
        { threshold: 0.1 }
      )
      observer.observe(node)
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [theme, productImgSrc]
  )

  const renderPreview = async () => {
    if (!previewRef.current) return
    const canvas = previewRef.current
    const ctx = canvas.getContext('2d')
    const { canvasW, canvasH, file, box } = theme

    canvas.width = canvasW
    canvas.height = canvasH

    try {
      // 1. Draw product image first (behind template)
      if (productImgSrc) {
        const productEl = await loadImage(productImgSrc)
        const { x, y, w, h } = box
        const scaleX = (w * 1.3) / productEl.naturalWidth
        const scaleY = (h * 1.3) / productEl.naturalHeight
        const scale = Math.max(scaleX, scaleY)
        const dw = productEl.naturalWidth * scale
        const dh = productEl.naturalHeight * scale
        const dx = x + (w - dw) / 2
        const dy = y + (h - dh) / 2
        ctx.drawImage(productEl, dx, dy, dw, dh)
      }
      // 2. Template PNG overlaid on top
      const template = await loadImage(file)
      ctx.drawImage(template, 0, 0, canvasW, canvasH)
      setPreviewReady(true)
    } catch {
      setHasError(true)
    }
  }

  const isStory = theme.canvasH > theme.canvasW

  return (
    <div
      ref={observerRef}
      className="relative group cursor-pointer rounded-xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-200 hover:-translate-y-1 bg-gray-100"
      onClick={() => !downloading && onDownload(theme, product)}
      title={`تحميل صورة ${product.name}`}
    >
      {/* Aspect-ratio container */}
      <div
        className="relative w-full overflow-hidden bg-gray-200"
        style={{ paddingBottom: isStory ? '177.78%' : '125%' }}
      >
        {/* The canvas scaled to fill */}
        <canvas
          ref={previewRef}
          className="absolute inset-0 w-full h-full object-cover"
          style={{ imageRendering: 'auto' }}
        />

        {/* Skeleton / fallback */}
        {!previewReady && !hasError && (
          <div className="absolute inset-0 flex items-center justify-center bg-gray-200 animate-pulse">
            <ImageIcon className="w-8 h-8 text-gray-400" />
          </div>
        )}
        {hasError && (
          <div className="absolute inset-0 flex flex-col items-center justify-center bg-gray-100 text-gray-400 text-xs text-center p-2">
            <ImageIcon className="w-6 h-6 mb-1" />
            <span>تعذّر التحميل</span>
          </div>
        )}

        {/* Hover overlay */}
        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center">
          {downloading ? (
            <div className="opacity-0 group-hover:opacity-100 transition-opacity">
              <div className="w-10 h-10 border-4 border-white border-t-transparent rounded-full animate-spin" />
            </div>
          ) : (
            <Download className="w-10 h-10 text-white opacity-0 group-hover:opacity-100 transition-opacity drop-shadow-lg" />
          )}
        </div>
      </div>

      {/* Product name */}
      <div className="p-2 bg-white">
        <p className="text-xs font-medium text-gray-700 truncate text-right" dir="rtl">
          {product.name}
        </p>
      </div>
    </div>
  )
}

// ─── Main Page ────────────────────────────────────────────────────────────────

const PAGE_SIZE = 10

export default function AdminProductThemes() {
  const { isAuthenticated, user, loading } = useAuth()
  const navigate = useNavigate()
  const [activeTheme, setActiveTheme] = useState(THEMES[0])
  const [downloadingId, setDownloadingId] = useState(null)
  const [search, setSearch] = useState('')
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE)

  const { data: productsData, isLoading: productsLoading } = useQuery({
    queryKey: ['product-themes-list'],
    queryFn: () => productService.getProducts({ status: 'published', limit: 200 }),
    enabled: !!isAuthenticated,
  })

  // Auth guard
  if (!loading && (!isAuthenticated || !user?.isAdmin)) {
    navigate('/login')
    return null
  }

  if (loading) return <LoadingSpinner />

  const allProducts = productsData?.products || productsData?.data || []
  const filteredProducts = allProducts.filter((p) =>
    p.name?.toLowerCase().includes(search.toLowerCase())
  )
  const visibleProducts = filteredProducts.slice(0, visibleCount)
  const hasMore = visibleCount < filteredProducts.length

  const handleDownload = async (theme, product) => {
    const key = `${theme.id}-${product.id}`
    setDownloadingId(key)
    try {
      const productImgSrc = getProductImageUrl(product)
      const dataUrl = await composeImage(theme, productImgSrc)
      const link = document.createElement('a')
      link.href = dataUrl
      const safeName = product.name?.replace(/\s+/g, '-') || product.id
      link.download = `${safeName}-${theme.label}-${theme.sublabel}.jpg`
      link.click()
    } catch (err) {
      console.error('Download failed', err)
      alert('تعذّر تحميل الصورة. تأكد من وجود ملفات الثيمات في папку themes')
    } finally {
      setDownloadingId(null)
    }
  }

  return (
    <div className="min-h-screen bg-gray-50" dir="rtl">
      {/* ── Header ── */}
      <div className="bg-white shadow-sm sticky top-0 z-20 border-b border-gray-200">
        <div className="max-w-screen-xl mx-auto px-4 py-3 flex items-center gap-3">
          <button
            onClick={() => navigate('/admin')}
            className="p-2 rounded-lg hover:bg-gray-100 text-gray-600 transition"
          >
            <ArrowRight className="w-5 h-5" />
          </button>
          <h1 className="text-xl font-bold text-gray-800 flex-1">تصدير صور المنتجات</h1>
          <span className="text-sm text-gray-500">{filteredProducts.length} منتج</span>
        </div>

        {/* ── Theme Selector ── */}
        <div className="max-w-screen-xl mx-auto px-4 pb-3 flex gap-3 overflow-x-auto">
          {THEMES.map((t) => (
            <button
              key={t.id}
              onClick={() => { setActiveTheme(t); setVisibleCount(PAGE_SIZE) }}
              className={`flex-shrink-0 px-5 py-2 rounded-full text-sm font-semibold transition-all border-2 ${
                activeTheme.id === t.id
                  ? 'bg-[#8B0000] text-white border-[#8B0000] shadow-md'
                  : 'bg-white text-gray-700 border-gray-300 hover:border-[#8B0000] hover:text-[#8B0000]'
              }`}
            >
              <span>{t.label}</span>
              <span
                className={`ml-2 text-xs px-1.5 py-0.5 rounded-full ${
                  activeTheme.id === t.id
                    ? 'bg-white/20 text-white'
                    : 'bg-gray-100 text-gray-500'
                }`}
              >
                {t.sublabel}
              </span>
            </button>
          ))}
        </div>

        {/* ── Search ── */}
        <div className="max-w-screen-xl mx-auto px-4 pb-3">
          <input
            type="text"
            placeholder="ابحث عن منتج..."
            value={search}
            onChange={(e) => { setSearch(e.target.value); setVisibleCount(PAGE_SIZE) }}
            className="w-full px-4 py-2 rounded-lg border border-gray-300 text-sm text-right focus:outline-none focus:ring-2 focus:ring-[#8B0000]/40"
          />
        </div>
      </div>

      {/* ── Theme Info Banner ── */}
      <div className="max-w-screen-xl mx-auto px-4 py-3">
        <div className="flex items-center gap-2 text-xs text-gray-500 bg-white rounded-lg px-4 py-2 border border-gray-100 shadow-sm">
          <ImageIcon className="w-4 h-4 text-[#8B0000]" />
          <span>
            {activeTheme.label} — {activeTheme.sublabel} —{' '}
            {activeTheme.canvasW}×{activeTheme.canvasH} بكسل
          </span>
          <span className="mr-auto text-gray-400">اضغط على أي صورة لتنزيلها بجودة عالية</span>
        </div>
      </div>

      {/* ── Grid ── */}
      <div className="max-w-screen-xl mx-auto px-4 pb-24">
        {productsLoading ? (
          <div className="flex justify-center py-20">
            <LoadingSpinner />
          </div>
        ) : filteredProducts.length === 0 ? (
          <div className="text-center py-20 text-gray-500">لا توجد منتجات</div>
        ) : (
          <>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
            {visibleProducts.map((product) => {
              const key = `${activeTheme.id}-${product.id}`
              return (
                <ThemeCard
                  key={key}
                  theme={activeTheme}
                  product={product}
                  onDownload={handleDownload}
                  downloading={downloadingId === key}
                />
              )
            })}
          </div>
          {hasMore && (
            <div className="flex justify-center mt-8">
              <button
                onClick={() => setVisibleCount((c) => c + PAGE_SIZE)}
                className="px-8 py-3 bg-[#8B0000] text-white rounded-full font-semibold text-sm hover:bg-[#6b0000] active:scale-95 transition-all shadow-md"
              >
                تحميل المزيد ({filteredProducts.length - visibleCount} متبقي)
              </button>
            </div>
          )}
          </>
        )}
      </div>

      {/* ── Setup Notice if themes folder is empty ── */}
      <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-30 pointer-events-none">
        <div className="bg-gray-800/90 text-white text-xs rounded-xl px-4 py-2 shadow-lg pointer-events-auto opacity-70 hover:opacity-100 transition-opacity max-w-xs text-center">
          ضع ملفات الثيمات في{' '}
          <code className="font-mono text-yellow-300">client/public/themes/</code>
          <br />
          بأسماء: theme1.png … theme4.png
        </div>
      </div>
    </div>
  )
}
